/**
 * Bandeau GLOBAL d'arrêt d'urgence de l'IA (SRF-TRV-030 ; § 4.10.1 ; planche `ArretUrgenceIA`).
 *
 * Monté une fois par la coquille, donc visible sur TOUTES les routes. Il nomme CHAQUE cause
 * active (« … sur toute la plateforme », « … dans org-demo »), avec motif, depuis et auteur, dit
 * ce qui reste possible, et ouvre « Pourquoi ? Cause et effets ». Il ne porte AUCUNE bascule :
 * le seul point de commande est le composant `ArretUrgenceIA`, monté à Plateforme › Arrêt
 * d'urgence et à Organisation › Gouvernance.
 */
import { useId, useState, type JSX } from 'react'
import { Link } from 'react-router-dom'
import { OctagonX } from 'lucide-react'

import { useT } from '@/i18n/language'
import {
  INTERROMPU, RESTE_POSSIBLE, RESTENT_POSSIBLES_PHRASE, detailCause, formaterInstant, titreCause,
} from '@/lib/arretIA'
import { useEtatArretIA } from '@/components/arret/useEtatArretIA'

export function BandeauArretIA({ roles, organisation }: {
  /** Rôles BRUTS du jeton : ils gouvernent ce que la passerelle laisse lire. */
  roles: readonly string[]
  organisation: string | undefined
}): JSX.Element | null {
  const { t } = useT()
  const etat = useEtatArretIA(roles)
  const [ouvert, setOuvert] = useState(false)
  const idDetail = useId()
  const constatSeul = etat.causes.length === 0 && !etat.lisible && etat.constate !== null
  if (etat.causes.length === 0 && !constatSeul) return null

  return (
    <section aria-label={t('arret.bandeauRegion', 'Arrêt d’urgence de l’IA')} data-testid="bandeau-arret-ia"
             className="border-b border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
      <div aria-live="polite" className="space-y-1">
        {etat.causes.map((cause) => (
          <p key={cause.portee} data-testid={`bandeau-arret-cause-${cause.portee}`} className="flex flex-wrap items-baseline gap-x-1.5">
            <OctagonX className="size-4 shrink-0 self-center text-destructive" aria-hidden="true" />
            <strong className="font-semibold">{titreCause(cause, organisation)}</strong>
            {detailCause(cause) ? <span>— {detailCause(cause)}</span> : null}
          </p>
        ))}
        {constatSeul ? (
          <p data-testid="bandeau-arret-constate" className="flex flex-wrap items-baseline gap-x-1.5">
            <OctagonX className="size-4 shrink-0 self-center text-destructive" aria-hidden="true" />
            <strong className="font-semibold">{t('arret.constateTitre', 'Requêtes IA interrompues')}</strong>
            <span>
              {t('arret.constateDetail', '— refus constaté à {{instant}} ; la cause est lisible par l’administration de votre organisation.',
                { instant: formaterInstant(etat.constate!.toISOString()) ?? '' })}
            </span>
          </p>
        ) : null}
      </div>
      <p className="mt-1 text-muted-foreground">
        {t('arret.restentPossibles', RESTENT_POSSIBLES_PHRASE)}{' '}
        <button type="button" data-testid="bandeau-arret-pourquoi" aria-expanded={ouvert} aria-controls={idDetail}
                onClick={() => setOuvert((o) => !o)}
                className="inline-flex min-h-10 items-center font-medium text-foreground underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {t('arret.pourquoi', 'Pourquoi ? Cause et effets')}
        </button>
      </p>
      <div id={idDetail} hidden={!ouvert} data-testid="bandeau-arret-detail" className="mt-2 grid gap-3 sm:grid-cols-2">
        <div>
          <p className="font-medium">{t('arret.interrompu', 'Interrompu')}</p>
          <ul className="ml-4 list-disc">{INTERROMPU.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
        <div>
          <p className="font-medium">{t('arret.restePossible', 'Reste possible')}</p>
          <ul className="ml-4 list-disc">{RESTE_POSSIBLE.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
        <p className="sm:col-span-2 text-muted-foreground">
          {t('arret.regle', 'L’IA est arrêtée si la plateforme OU votre organisation l’a arrêtée : lever l’une ne rétablit rien tant que l’autre reste active.')}
          {etat.superAdmin ? (
            <> <Link to="/plateforme/arret-ia" data-testid="bandeau-arret-lien-plateforme" className="font-medium underline underline-offset-2">
              {t('arret.lienPlateforme', 'Plateforme › Arrêt d’urgence de l’IA')}</Link></>
          ) : null}
          {etat.administrateurOrganisation ? (
            <> <Link to="/org/gouvernance" data-testid="bandeau-arret-lien-organisation" className="font-medium underline underline-offset-2">
              {t('arret.lienOrganisation', 'Organisation › Gouvernance et approbations')}</Link></>
          ) : null}
        </p>
      </div>
    </section>
  )
}
